// עדכונים בלתי משתנים למסמך (לכל שינוי מסמך חדש — נדרש להיסטוריית Undo).
import { applyOps, type BannerDoc, type EditOp, type Layer } from '@bb/schema';

export function updateLayer(doc: BannerDoc, id: string, fn: (l: Layer) => Layer | void): BannerDoc {
  return {
    ...doc,
    layers: doc.layers.map((l) => {
      if (l.id !== id) return l;
      const c = structuredClone(l);
      const r = fn(c);
      return r ?? c;
    }),
  };
}

export function updateDoc(doc: BannerDoc, fn: (d: BannerDoc) => void): BannerDoc {
  const d = structuredClone(doc);
  fn(d);
  return d;
}

/** החלת פעולות מוגדרות (אותן פעולות שה-AI מחזיר) — בידי המשתמש, בלי AI */
export function userOps(doc: BannerDoc, ops: EditOp[]): { doc: BannerDoc; error?: undefined } | { doc?: undefined; error: string } {
  try { return { doc: applyOps(doc, ops, { actor: 'user' }) }; } catch (e) { return { error: e instanceof Error ? e.message : String(e) }; }
}

export function trackOf(doc: BannerDoc, layerId: string) {
  return doc.animationTracks.find((t) => t.layerId === layerId) ?? null;
}

/** שכפול שכבה כעריכה ידנית של המסמך (מזהה חדש, הזחה קלה, מעל המקור) */
export function duplicateLayer(doc: BannerDoc, id: string): { doc: BannerDoc; newId: string } | null {
  const src = doc.layers.find((l) => l.id === id);
  if (!src) return null;
  const ids = new Set(doc.layers.map((l) => l.id));
  let n = 1;
  let nid = `${src.id}_copy`.slice(0, 58);
  while (ids.has(nid)) nid = `${src.id.slice(0, 54)}_copy${++n}`;
  const copy = structuredClone(src) as Layer;
  copy.id = nid;
  copy.name = `${src.name} (עותק)`.slice(0, 80);
  copy.x = src.x + 10; copy.y = src.y + 10;
  copy.zIndex = Math.min(999, Math.max(...doc.layers.map((l) => l.zIndex)) + 1);
  // שכפול של טקסט מאושר אינו "הטקסט המאושר" עצמו
  if (copy.type === 'text' || copy.type === 'button') { copy.content.copyKey = null; copy.content.locked = false; }
  const tr = trackOf(doc, id);
  const d = structuredClone(doc);
  d.layers.push(copy);
  if (tr) d.animationTracks.push({ ...structuredClone(tr), layerId: nid });
  return { doc: d, newId: nid };
}

/** סידור z: העלאה/הורדה בשכבה אחת ביחס לשכנה */
export function moveZ(doc: BannerDoc, id: string, dir: 1 | -1): BannerDoc {
  const sorted = [...doc.layers].sort((a, b) => a.zIndex - b.zIndex);
  const i = sorted.findIndex((l) => l.id === id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= sorted.length) return doc;
  // מספור רציף ואז החלפה — פותר zIndex שווים
  const order = sorted.map((l) => l.id);
  [order[i], order[j]] = [order[j], order[i]];
  const z = new Map(order.map((lid, k) => [lid, k]));
  return { ...doc, layers: doc.layers.map((l) => ({ ...l, zIndex: z.get(l.id)! })) };
}

export const r1 = (n: number) => Math.round(n);
